/**
 * Recursive diff dispatcher.
 *
 * Routes each node pair to the appropriate diff algorithm based on
 * the element's tag name (classified via schema.ts).
 */
import type { ChildNode, Element, Text } from 'domhandler';

import {
  isElementAtom,
  isElementInlineContainer,
  isInlineElement,
  isTableElement,
  isDocumentSection,
  getAttrs,
} from '../schema';
import { attrsEqual } from './compare';
import { diffInlineContent } from './inlineDiff';
import { diffSequence, getElementChildren, elementsMatch } from './matcher';
import { diffTableNode } from './tableDiff';
import type { TDiffNode, TDiffResult } from '../types';

// ---------------------------------------------------------------------------
// Build inserted / deleted subtrees
// ---------------------------------------------------------------------------

const buildInsertedFromChild = (node: ChildNode): TDiffNode | null => {
  if (node.type === 'text') {
    const text = (node as Text).data;
    if (!text) return null;
    return {
      type: 'text',
      change: 'inserted',
      attrs: {},
      marks: [],
      textSegments: [{ text, change: 'inserted', marks: [] }],
      isText: true,
      isInline: true,
    };
  }
  if (node.type !== 'tag') return null;
  return buildInserted(node as Element);
};

export const buildInserted = (el: Element): TDiffNode => {
  const children = el.children.map(buildInsertedFromChild).filter((c): c is TDiffNode => c !== null);
  return {
    type: el.tagName,
    change: 'inserted',
    attrs: getAttrs(el),
    marks: [],
    children,
    isAtom: isElementAtom(el),
    isInline: isInlineElement(el.tagName),
    currElement: el,
  };
};

const buildDeletedFromChild = (node: ChildNode): TDiffNode | null => {
  if (node.type === 'text') {
    const text = (node as Text).data;
    if (!text) return null;
    return {
      type: 'text',
      change: 'deleted',
      attrs: {},
      marks: [],
      textSegments: [{ text, change: 'deleted', marks: [] }],
      isText: true,
      isInline: true,
    };
  }
  if (node.type !== 'tag') return null;
  return buildDeleted(node as Element);
};

export const buildDeleted = (el: Element): TDiffNode => {
  const children = el.children.map(buildDeletedFromChild).filter((c): c is TDiffNode => c !== null);
  return {
    type: el.tagName,
    change: 'deleted',
    attrs: getAttrs(el),
    marks: [],
    children,
    isAtom: isElementAtom(el),
    isInline: isInlineElement(el.tagName),
    prevElement: el,
  };
};

/** Atoms are structurally indivisible - compare attributes and tag name. */
const diffAtomNode = (curr: Element, prev: Element): TDiffNode => {
  const attrsChanged = !attrsEqual(curr.tagName, getAttrs(curr), getAttrs(prev));
  const typeChanged = curr.tagName !== prev.tagName;
  return {
    type: curr.tagName,
    change: attrsChanged || typeChanged ? 'modified' : 'unchanged',
    attrs: getAttrs(curr),
    prevAttrs: attrsChanged ? getAttrs(prev) : undefined,
    marks: [],
    isAtom: true,
    isInline: isInlineElement(curr.tagName),
    currElement: curr,
    prevElement: prev,
  };
};

// ---------------------------------------------------------------------------
// Section-transparent matching
// ---------------------------------------------------------------------------
//
// Sections are structural wrappers (landscape pages). Flatten before LCS so
// children match across section boundaries, then reconstruct wrappers after.

type TFlatChild = { element: Element; section: Element | null };
type TSectionEntry = { node: TDiffNode; prevSection: Element | null; currSection: Element | null };

const flattenSections = (children: Element[]): TFlatChild[] =>
  children.flatMap((child): TFlatChild[] =>
    isDocumentSection(child)
      ? getElementChildren(child).map((gc) => ({ element: gc, section: child }))
      : [{ element: child, section: null }]
  );

const buildSectionBadge = (label: string): TDiffNode => ({
  type: 'section-badge',
  change: 'modified',
  attrs: { label },
  marks: [],
  isAtom: true,
});

const buildSectionWrapper = (prevSection: Element, currSection: Element, children: TDiffNode[]): TDiffNode => {
  const attrsChanged = !attrsEqual('section', getAttrs(currSection), getAttrs(prevSection));
  const hasChildChanges = children.some((c) => c.change !== 'unchanged');

  return {
    type: currSection.tagName,
    change: attrsChanged || hasChildChanges ? 'modified' : 'unchanged',
    attrs: getAttrs(currSection),
    prevAttrs: attrsChanged ? getAttrs(prevSection) : undefined,
    marks: [],
    children,
    currElement: currSection,
    prevElement: prevSection,
  };
};

/**
 * Regroup flat diff entries back into section wrappers with badges.
 *
 * Groups consecutive entries by their currSection (or prevSection for removals).
 * Bare entries between section members are absorbed via lookahead so that e.g.
 * a deleted paragraph between two matched section children doesn't split the group.
 * Tracks distinct prevSections per group to detect merges (2+ prev → 1 curr).
 */
const regroupSectionEntries = (entries: TSectionEntry[]): TDiffNode[] => {
  const children: TDiffNode[] = [];
  let i = 0;

  while (i < entries.length) {
    const { prevSection, currSection } = entries[i];

    // Bare entry - no section on either side
    if (!prevSection && !currSection) {
      children.push(entries[i].node);
      i++;
      continue;
    }

    // Collect all entries belonging to this section group
    const groupKey = currSection ?? prevSection;
    const group: TDiffNode[] = [entries[i].node];
    const prevSections = new Set<Element>();
    if (prevSection) prevSections.add(prevSection);

    let j = i + 1;
    while (j < entries.length) {
      const entryKey = entries[j].currSection ?? entries[j].prevSection;

      if (entryKey === groupKey) {
        if (entries[j].prevSection) prevSections.add(entries[j].prevSection!);
        group.push(entries[j].node);
        j++;
        continue;
      }

      // Absorb bare entry only if a later entry still belongs to this section
      if (!entries[j].currSection && !entries[j].prevSection) {
        let hasLater = false;
        for (let k = j + 1; k < entries.length; k++) {
          if ((entries[k].currSection ?? entries[k].prevSection) === groupKey) {
            hasLater = true;
            break;
          }
        }
        if (hasLater) {
          group.push(entries[j].node);
          j++;
          continue;
        }
      }

      break;
    }

    // Emit the section wrapper with appropriate badge
    const isMerge = !!currSection && prevSections.size > 1;

    if (prevSection && currSection) {
      const badge = isMerge ? [buildSectionBadge('landscape pages merged')] : [];
      children.push(buildSectionWrapper(prevSection, currSection, [...badge, ...group]));
    } else if (currSection) {
      const badge = isMerge ? 'landscape pages merged' : 'landscape page added';
      children.push(buildSectionWrapper(currSection, currSection, [buildSectionBadge(badge), ...group]));
    } else {
      children.push(buildSectionBadge('landscape page removed'), ...group);
    }

    i = j;
  }

  return children;
};

/**
 * Generic container diff - child sequence LCS with section-transparent matching.
 *
 * When children include document sections, they are flattened before matching
 * so content matches across section boundaries, then wrappers are reconstructed.
 * No move operator - reorders are represented as delete + insert from the LCS.
 */
const diffContainerNode = (curr: Element, prev: Element): TDiffNode => {
  const currChildren = getElementChildren(curr);
  const prevChildren = getElementChildren(prev);

  // Flatten sections so their children participate in LCS directly
  const hasSections = currChildren.some(isDocumentSection) || prevChildren.some(isDocumentSection);
  const currFlat: TFlatChild[] = hasSections
    ? flattenSections(currChildren)
    : currChildren.map((e) => ({ element: e, section: null }));
  const prevFlat: TFlatChild[] = hasSections
    ? flattenSections(prevChildren)
    : prevChildren.map((e) => ({ element: e, section: null }));

  // LCS on flattened children
  const operations = diffSequence(prevFlat, currFlat, (p, c) => elementsMatch(p.element, c.element));

  // Map LCS operations to diff nodes, preserving section metadata
  const entries: TSectionEntry[] = operations.map((op): TSectionEntry => {
    if (op.type === 'equal') {
      return {
        node: diffNode(op.currItem.element, op.prevItem.element),
        prevSection: op.prevItem.section,
        currSection: op.currItem.section,
      };
    }
    if (op.type === 'insert') {
      return {
        node: buildInserted(op.currItem.element),
        prevSection: null,
        currSection: op.currItem.section,
      };
    }
    return {
      node: buildDeleted(op.prevItem.element),
      prevSection: op.prevItem.section,
      currSection: null,
    };
  });

  // Reconstruct section wrappers (no-op when there are no sections)
  const children = hasSections ? regroupSectionEntries(entries) : entries.map((e) => e.node);

  const attrsChanged = !attrsEqual(curr.tagName, getAttrs(curr), getAttrs(prev));
  const typeChanged = curr.tagName !== prev.tagName;
  const hasChildChanges = children.some((child) => child.change !== 'unchanged');

  return {
    type: curr.tagName,
    change: attrsChanged || typeChanged || hasChildChanges ? 'modified' : 'unchanged',
    attrs: getAttrs(curr),
    prevAttrs: attrsChanged ? getAttrs(prev) : undefined,
    marks: [],
    children,
    currElement: curr,
    prevElement: prev,
  };
};

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

/**
 * Recursive diff dispatcher.
 *
 *   if both are table-related → table-specific ordered diff
 *   if both are atoms         → compare attrs
 *   if both are inline containers → flatten to tokens, run inline LCS
 *   else                      → generic container diff (child sequence LCS)
 */
export const diffNode = (curr: Element, prev: Element): TDiffNode => {
  if (isTableElement(curr.tagName) && isTableElement(prev.tagName)) {
    return diffTableNode(curr, prev, diffNode, buildInserted, buildDeleted);
  }

  if (isElementAtom(curr) && isElementAtom(prev)) {
    return diffAtomNode(curr, prev);
  }

  if (isElementInlineContainer(curr) && isElementInlineContainer(prev)) {
    return diffInlineContent(curr, prev, diffNode, buildInserted, buildDeleted);
  }

  return diffContainerNode(curr, prev);
};

export const diffDocuments = (currRoot: Element, prevRoot: Element): TDiffResult => {
  const root = diffContainerNode(currRoot, prevRoot);
  const stats = { inserted: 0, deleted: 0, modified: 0, unchanged: 0 };

  const visit = (node: TDiffNode) => {
    stats[node.change] += 1;
    node.children?.forEach(visit);
  };

  visit(root);

  return { root, stats };
};
